<template>
	<div class="cassie-vertical-md mt-1">
		<SectionCard v-if="scanResults">
			<template #title>
				URL Scan Results
			</template>
			<template #subtitle>
				{{ scanCounts }}
			</template>
			<template #body>
				<v-row dense>
					<v-spacer />
					<v-col cols="4">
						<Doughnut
							:chart-data="chartData"
							:chart-options="chartOptions"
							chart-id="url-scan-results-doughnut"
						/>
					</v-col>
					<v-spacer />
				</v-row>
				<v-row dense>
					<v-spacer />
					<v-col cols="6">
						<div id="url-scan-results-legend" />
					</v-col>
					<v-spacer />
				</v-row>
			</template>
		</SectionCard>
		<SectionCard v-if="scanResults">
			<template #title>
				Scanned URL {{ scanResults.resultDetails.length ? 'Failure ' : '' }}Breakdown
			</template>
			<template #body>
				<v-row
					v-if="scanResults.resultDetails.length"
					dense
				>
					<v-col cols="12">
						<DataTable
							:headers="urlFailureTableHeaders"
							:items="scanResults.resultDetails"
							:hide-default-footer="scanResults.resultDetails.length <= 15"
							:interactable="false"
						/>
					</v-col>
				</v-row>
				<v-divider
					v-if="scanResults.resultDetails.length"
				/>
				<v-row dense>
					<div class="button-row">
						<span>
							For a detailed look at which URLs were scanned please download the URL status report
						</span>
					</div>
				</v-row>
			</template>
		</SectionCard>
		<SectionCard v-if="scanDetails">
			<template #title>
				Scan Configuration
			</template>
			<template #subtitle>
				The details entered into the scanner when this scan was requested
			</template>
			<template #body>
				<v-row dense>
					<v-col cols="6">
						<TextField
							:value="scanDetails.scanName"
							label="Scan Name"
							view-only
							disabled
						/>
					</v-col>
					<v-col cols="6">
						<TextField
							:value="scanDetails.brand"
							:label="'Brand' | useLabels"
							view-only
							disabled
						/>
					</v-col>
				</v-row>
				<v-row dense>
					<v-col cols="6">
						<TextField
							:value="scanDetails.emailAddress"
							label="Email Address"
							view-only
							disabled
						/>
					</v-col>
				</v-row>
				<v-divider />
				<v-row dense>
					<v-col cols="6">
						<TextField
							:value="scanTypes[scanDetails.scanType]"
							label="Type of Scan"
							disabled
							view-only
						/>
					</v-col>
					<v-col cols="6">
						<TextField
							:value="resourceDisplay"
							:label="scanTypeLabels[scanDetails.scanType]"
							disabled
							view-only
						/>
					</v-col>
				</v-row>
				<v-row dense>
					<v-col cols="6">
						<TextField
							:value="printBool(!scanDetails.deepCrawl)"
							label="Limited to the Base URL"
							disabled
							view-only
						/>
					</v-col>
					<v-col cols="6">
						<TextField
							:value="pausePeriodDisplay"
							label="Pause Period per URL Scanned"
							disabled
							view-only
						/>
					</v-col>
				</v-row>
				<v-row dense>
					<v-col cols="6">
						<TextField
							:value="scanDetails.userAgent"
							label="Device & Browser Type"
							disabled
							view-only
						/>
					</v-col>
				</v-row>
			</template>
		</SectionCard>
		<SectionCard v-if="scanDetails">
			<template #title>
				Scan Schedule
			</template>
			<template #subtitle>
				When the scan was scheduled to take place and if this was on a recurring basis
			</template>
			<template #body>
				<v-row dense>
					<v-col cols="6">
						<TextField
							:value="printScanScheduled(scanDetails.scheduleDetails.scheduled)"
							label="Schedule Scan"
							disabled
							view-only
						/>
					</v-col>
				</v-row>
				<v-row dense>
					<v-col cols="12">
						<span>
							This scan took place {{ scanDateFormatted(scanDetails.scheduleDetails.lastScan) }}
						</span>
					</v-col>
				</v-row>
				<v-row
					v-if="showNextScan"
					dense
				>
					<v-col cols="12">
						<span>
							Next scheduled scan {{ scanDateFormatted(scanDetails.scheduleDetails.nextScan) }}
						</span>
					</v-col>
				</v-row>
			</template>
		</SectionCard>
		<SectionCard v-if="scanDetails">
			<template #title>
				Advanced Features
			</template>
			<template #body>
				<v-row dense>
					<v-col cols="6">
						<TextField
							:value="actionSelectorValue"
							label="Cookie Banner Interaction Button Selector"
							disabled
							view-only
						/>
					</v-col>
				</v-row>
				<v-divider />
				<v-row dense>
					<v-col cols="6">
						<DataTable
							:headers="allowedDomainsTableHeader"
							:items="mappedAllowedDomains"
							hide-default-footer
							class="elevation-1"
							:interactable="false"
						/>
					</v-col>
				</v-row>
				<v-divider />
				<v-row dense>
					<v-col cols="6">
						<TextField
							:value="pageTimeoutDisplay"
							label="Page Timeout"
							disabled
							view-only
						/>
					</v-col>
					<v-col cols="6">
						<TextField
							:value="printBool(scanDetails.headless)"
							label="Headless Mode"
							disabled
							view-only
						/>
					</v-col>
				</v-row>
				<v-row
					dense
				>
					<v-col
						v-if="scanDetails.hasLoginTemplates"
						cols="6"
					>
						<TextField
							:value="scanDetails.loginTemplate"
							label="Scan Behind Login Template"
							disabled
							view-only
						/>
					</v-col>
					<v-col
						cols="6"
					>
						<TextField
							:value="printBool(scanDetails.gpcEnabled)"
							label="Send GPC Signal"
							disabled
							view-only
						/>
					</v-col>
				</v-row>
			</template>
		</SectionCard>
	</div>
</template>

<script>
import { Doughnut } from 'vue-chartjs/legacy'
import { format } from 'date-fns'
import {
	Chart as ChartJS,
	Title,
	Tooltip,
	Legend,
	ArcElement,
	CategoryScale
} from 'chart.js'
import SectionCard from '../../../../../../../shared/components/section-card.vue'
import TextField from '../../../../../../../shared/components/text-field.vue'
import { getScanActivity, downloadScanFile, validateCsvExists, downloadCsvFromS3 } from '../../../../../../../shared/utils/api/site-scanner.js'
import { downloadTypeEnum } from '../download-type-enums.js'
import downloadCSVMixin from '../../../../../../../shared/mixins/download-csv.js'
import DataTable from '../../../../../../../shared/components/data-table.vue'
import htmlLegendPluginMixin from '../../../../../../../shared/mixins/html-legend-plugin.js'
import { DATE_TIME_FORMAT_NO_SECONDS } from '../../../../../../../shared/utils/date-formatting.js'
import { getServerTimezoneInfo } from '../../../../../../../shared/utils/api/server-timezone.js'

export default {
	components: {
		SectionCard,
		TextField,
		DataTable,
		Doughnut
	},
	mixins: [htmlLegendPluginMixin, downloadCSVMixin],
	props: {
		scanGuid: String,
		scanActivityCsvExists: Boolean
	},
	setup () {
		return {
			downloadTypeEnum
		}
	},
	data () {
		return {
			serverTimeInfo: null,
			scanDetails: null,
			scanResults: null,
			doesCsvExist: false,
			chartOptions: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: {
						display: false
					},
					title: {
						display: false
					}
				}
			}
		}
	},
	computed: {
		pausePeriodDisplay () {
			const pausePeriodSeconds = this.scanDetails.pausePeriodPerUrl /	1000
			if (pausePeriodSeconds === 1) {
				return `${pausePeriodSeconds.toFixed(1)} Second`
			}
			if (pausePeriodSeconds === 0) {
				return `${pausePeriodSeconds.toFixed(0)} Seconds`
			}
			return `${pausePeriodSeconds.toFixed(1)} Seconds`
		},
		showNextScan () {
			const nextRunDate = new Date(this.scanDetails.scheduleDetails.nextScan)
			return Date.now() < nextRunDate
		},
		totalResults () {
			return Object.values(this.scanResults.resultSummary)
				.reduce((a, b) => a + b, 0)
		},
		successfulCount () {
			return this.scanResults.resultSummary.successful
		},
		failedCount () {
			return this.scanResults.resultSummary.failed
		},
		unableCount () {
			return this.scanResults.resultSummary.unableToScan
		},
		allowedDomainsTableHeader () {
			return [{ text: 'Allowed Domains', value: 'domain', width: '100%' }]
		},
		urlFailureTableHeaders () {
			return [
				{ text: 'Message', value: 'message', width: '80%' },
				{ text: 'Count', value: 'count', width: '20%' }
			]
		},
		pageTimeoutDisplay () {
			const seconds = Math.floor(this.scanDetails.pageTimeout / 1000)
			if (seconds === 1) {
				return `${seconds} Second`
			}
			return `${seconds} Seconds`
		},
		mappedAllowedDomains () {
			return this.scanDetails.allowedDomains
				.map(domain => ({ domain }))
				.filter(({ domain }) => domain && domain !== 'null')
		},
		labels () {
			return {
				successful: 'Successful',
				failed: 'Failed',
				unableToScan: 'Unable to scan'
			}
		},
		legendColours () {
			const colorPalette = [
				'var(--global-primary)', // Blue Steel, it's like a look, but not a look
				'#0B3142', // Prussian Blue
				'#7D8CA3' // Cool Gray
			]
			let colorData = []
			if (this.scanResults.resultSummary.successful) {
				colorData = [...colorData, colorPalette[0]]
			}
			if (this.scanResults.resultSummary.failed) {
				colorData = [...colorData, colorPalette[1]]
			}
			if (this.scanResults.resultSummary.unableToScan) {
				colorData = [...colorData, colorPalette[2]]
			}
			return colorData
		},
		chartData () {
			const labels = []
			const datasets = [
				{
					data: [],
					backgroundColor: this.legendColours
				}
			]
			for (const key of Object.keys(this.scanResults.resultSummary)) {
				const count = this.scanResults.resultSummary[key]
				if (count === 0) continue
				labels.push(this.labels[key])
				datasets[0].data.push(count)
			}
			return { labels, datasets }
		},
		scanTypes () {
			return {
				0: 'URL Scan',
				1: 'Sitemap file',
				2: 'Sitemap URL'
			}
		},
		scanTypeLabels () {
			return {
				0: 'Base URL',
				1: 'Sitemap file',
				2: 'Base URL'
			}
		},
		resourceDisplay () {
			if (this.scanDetails.scanType === 0) {
				return this.scanDetails.domain
			} else if (this.scanDetails.scanType === 1) {
				return this.scanDetails.xmlSiteMapOriginalFileName
			} else {
				return this.scanDetails.sitemapUrl
			}
		},
		showAdvancedFeatures () {
			const advancedFeatures = {
				actionSelector: this.scanDetails.actionSelector,
				allowedDomains: this.mappedAllowedDomains,
				pageTimeout: this.scanDetails.pageTimeout,
				headless: this.scanDetails.headless,
				loginTemplateName: this.scanDetails.loginTemplateName,
				gpcEnabled: this.scanDetails.gpcEnabled
			}
			return advancedFeatures.actionSelector ||
				(advancedFeatures.allowedDomains && advancedFeatures.allowedDomains.length > 1) ||
				advancedFeatures.pageTimeout !== 5000 ||
				advancedFeatures.headless ||
				advancedFeatures.loginTemplateName ||
				advancedFeatures.gpcEnabled
		},
		scanCounts () {
			let baseString = `Found ${this.totalResults} URLs`
			if (this.successfulCount) {
				baseString += `, ${this.successfulCount} Successful`
			}
			if (this.failedCount) {
				baseString += `, ${this.failedCount} Failed`
			}
			if (this.unableCount) {
				baseString += `, ${this.unableCount} Unable to scan`
			}
			return baseString
		},
		actionSelectorValue () {
			return this.scanDetails.actionSelector ? this.scanDetails.actionSelector : 'No Cookie Banner Interaction Specified'
		}
	},
	created () {
		ChartJS.register(
			Title,
			Tooltip,
			Legend,
			ArcElement,
			CategoryScale,
			this.htmlLegendPlugin('url-scan-results-legend', 'row'))
	},
	async mounted () {
		await this.setScanActivityInfo()
		ChartJS.unregister(this.htmlLegendPlugin('url-scan-results-legend', 'row'))
		await this.getCsvExists()
	},
	methods: {
		printBool (bool) {
			return bool ? 'Yes' : 'No'
		},
		printScanScheduled (bool) {
			return bool ? 'Recurring Scan' : 'Single Scan'
		},
		async downloadCsv (downloadType) {
			const response = await downloadScanFile(this.scanGuid, downloadType, null)
			downloadCsvFromS3(response.data.responseFileName)
		},
		async getCsvExists () {
			const response = await validateCsvExists(this.scanGuid)
			this.doesCsvExist = response.scanActivityCsvExists
		},
		scanDateFormatted (date) {
			return `${format((new Date(date)).getTime(), DATE_TIME_FORMAT_NO_SECONDS)} ${this.serverTimeInfo.serverTimezone}`
		},
		async setScanActivityInfo () {
			const { scanDetails, scanResults } = await getScanActivity(this.scanGuid)
			const timezoneResponse = await getServerTimezoneInfo()
			this.serverTimeInfo = timezoneResponse.data
			this.scanDetails = scanDetails
			scanResults.resultDetails = scanResults.resultDetails
				.slice()
				.sort((a, b) => b.count - a.count)
			this.scanResults = scanResults
		}
	}
}
</script>

<style scoped>
.button-row {
	display: flex;
	flex-direction: row;
	align-content: space-between;
	justify-content: space-between;
	min-width: 100%;
}
</style>
